import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Book,
  CloudUpload,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { AuthContext } from '../../../contexts/AuthContext';
import axios from '../../../api/axios';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../../../components/Dashboard/DashboardLayout';
import SidebarWithAccordion from '../../../components/Dashboard/SidebarWithAccordion';
import { createTeacherSidebarConfig } from '../../../components/Dashboard/TeacherSidebarConfig';
import { createStudentSidebarConfig } from '../../../components/Dashboard/StudentSidebarConfig';
import NotebookAgentAvatar from '../../../components/Avatar/NotebookAgentAvatar';

// 生成固定的随机图片（基于笔记标题）
const getNotebookImage = (notebookTitle) => {
  return `https://picsum.photos/seed/${encodeURIComponent(notebookTitle)}/400/200`;
};

// 毛玻璃卡片样式（与 NotebookCardGrid 统一）
const cardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  border: 'none',
  boxShadow: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s',
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: (theme) =>
      theme.palette.mode === 'dark'
        ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)'
        : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
    pointerEvents: 'none',
    zIndex: 1,
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: 6,
  },
};

const cardContentSx = {
  position: 'relative',
  zIndex: 1,
  flexGrow: 1,
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: (theme) =>
      theme.palette.mode === 'dark'
        ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)'
        : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
    pointerEvents: 'none',
    zIndex: 2,
  },
};

const cardActionsSx = {
  position: 'relative',
  zIndex: 1,
  p: 2,
  pt: 0,
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: (theme) =>
      theme.palette.mode === 'dark'
        ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)'
        : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
    pointerEvents: 'none',
    zIndex: 2,
  },
};

function NotebookList() {
  const navigate = useNavigate();
  const { courseCode } = useParams();
  const location = useLocation();
  const { selectedCourse, user, logout } = useContext(AuthContext);
  const { t } = useTranslation();
  const [notebooks, setNotebooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notebookToDelete, setNotebookToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [categoriesOpen, setCategoriesOpen] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [selectedNotebook, setSelectedNotebook] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [editingNotebook, setEditingNotebook] = useState(null);
  const [newNotebookTitle, setNewNotebookTitle] = useState('');
  const [newNotebookDescription, setNewNotebookDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  useEffect(() => {
    fetchNotebooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseCode, selectedCourse]);

  useEffect(() => {
    if (uploadSuccess) {
      // Refresh notebook list after successful upload
      const timer = setTimeout(() => {
        fetchNotebooks();
        setUploadSuccess(null);
      }, 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadSuccess]);

  const fetchNotebooks = async () => {
    try {
      setLoading(true);
      const code = courseCode || selectedCourse?.code;
      if (!code) {
        console.warn('No course code available');
        setNotebooks([]);
        return;
      }
      const response = await axios.get(`/notes/course/${code.toUpperCase()}`);
      setNotebooks(response.data || []);
    } catch (error) {
      console.error('Error fetching notebooks:', error);
      setNotebooks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNotebookClick = (notebookId) => {
    navigate(`/notebook/${notebookId}`);
  };

  /**
   * 处理文件选择
   * 
   * 工作流程（按照 docs/PLATFORM_WORKFLOW.md）：
   * 1. 验证文件类型（支持 .json, .md, .txt, .markdown）
   * 2. 对于 JSON 文件：
   *    - 读取文件内容
   *    - 验证 JSON 格式
   *    - 如果格式错误，显示错误信息
   * 3. 对于其他格式（.md, .txt, .markdown）：
   *    - 直接设置文件（由后端处理转换）
   *    - 后端会调用 Notebook Agent 进行转换
   * 
   * @param {Event} event - 文件选择事件
   * @see docs/PLATFORM_WORKFLOW.md - 用户工作流程
   */
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // 步骤 1: 验证文件类型
      const allowedExtensions = ['.json', '.md', '.txt', '.markdown'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedExtensions.includes(fileExtension)) {
        setUploadError('只支持 JSON、Markdown 或文本格式的文件');
        setSelectedFile(null);
        return;
      }
      
      // 步骤 2: 对于 JSON 文件，验证 JSON 格式
      if (fileExtension === '.json') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target.result;
            JSON.parse(content);
            setSelectedFile(file);
            setUploadError(null);
          } catch (error) {
            setUploadError(`JSON 格式错误: ${error.message}`);
            setSelectedFile(null);
          }
        };
        reader.readAsText(file);
      } else {
        // 步骤 3: 对于非 JSON 文件（.md, .txt, .markdown），直接设置文件
        // 后端会自动检测格式并调用 Notebook Agent 进行转换
        setSelectedFile(file);
        setUploadError(null);
      }
    }
  };

  /**
   * 处理文件上传
   * 
   * 工作流程（按照 docs/PLATFORM_WORKFLOW.md）：
   * 1. 验证文件已选择
   * 2. 验证课程代码
   * 3. 使用 FormData 上传文件
   *    - 传递文件（notebook）
   *    - 传递课程代码（courseCode）
   * 4. 后端处理：
   *    - JSON 文件：直接验证格式并导入
   *    - 其他格式：调用 Notebook Agent 转换后导入
   * 5. 显示上传结果
   * 6. 成功后刷新笔记本列表
   * 
   * @see docs/PLATFORM_WORKFLOW.md - 用户工作流程和系统处理流程
   */
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('请选择要上传的文件');
      return;
    }

    const code = courseCode || selectedCourse?.code;
    if (!code) {
      setUploadError('请先选择课程');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    // 显示提示信息：非 JSON 文件转换可能需要较长时间
    const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
    if (fileExtension !== '.json') {
      setUploadSuccess('正在转换文件，这可能需要几分钟时间，请耐心等待...');
    }

    try {
      // 步骤 3: 使用 FormData 上传文件
      const formData = new FormData();
      formData.append('notebook', selectedFile);
      formData.append('courseCode', code.toUpperCase());

      // 步骤 4: 发送上传请求
      // 后端会自动检测文件格式：
      // - JSON 文件：直接验证格式并导入
      // - 其他格式（.md, .txt, .markdown）：调用 Notebook Agent 转换后导入
      // 注意：笔记转换可能需要较长时间（调用 AI API），设置超时时间为 10 分钟
      const response = await axios.post('/notes/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 10 * 60 * 1000, // 10 分钟超时（600000 毫秒）
      });

      // 步骤 5: 显示上传结果
      setUploadSuccess(`笔记 "${response.data.notebook.title}" 上传成功！`);
      
      // 步骤 6: 关闭对话框并刷新笔记本列表
      setTimeout(() => {
        setUploadDialogOpen(false);
        setSelectedFile(null);
        fetchNotebooks();
      }, 1500);
    } catch (error) {
      console.error('Upload error:', error);
      // 步骤 5: 显示错误信息
      let errorMessage = '上传失败，请检查文件格式和网络连接';
      
      // 处理超时错误
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = '请求超时。笔记转换可能需要较长时间，请稍后重试或检查网络连接。';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        if (error.response.data.details) {
          errorMessage += `: ${error.response.data.details}`;
        }
      } else if (error.response?.data?.details) {
        errorMessage = error.response.data.details;
      }
      
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadDialog = () => {
    if (!uploading) {
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setUploadError(null);
      setUploadSuccess(null);
    }
  };

  const handleDeleteClick = (notebook, e) => {
    e.stopPropagation();
    setNotebookToDelete(notebook);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!notebookToDelete) return;

    setDeleting(true);
    try {
      await axios.delete(`/notes/${notebookToDelete.id}`);
      setDeleteDialogOpen(false);
      setNotebookToDelete(null);
      // Refresh notebook list
      fetchNotebooks();
    } catch (error) {
      console.error('Delete error:', error);
      alert(error.response?.data?.error || '删除失败，请重试');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setNotebookToDelete(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Drawer content - Use shared sidebar config for students, SidebarWithAccordion for teachers
  const drawerContent = user?.role === 'student' ? (
    (() => {
      const sidebarConfig = createStudentSidebarConfig({
        navigate,
        user,
        selectedCourse,
        onLogout: handleLogout,
        t, // 传入翻译函数
        currentPath: location.pathname
      });
      const handleSettingsClick = () => {
        navigate('/settings');
      };
      return <SidebarWithAccordion {...sidebarConfig} onSettingsClick={handleSettingsClick} />;
    })()
  ) : (
    (() => {
      const sidebarConfig = createTeacherSidebarConfig({
        navigate,
        user,
        selectedCourse,
        onLogout: handleLogout,
        currentPath: location.pathname
      });
      const handleSettingsClick = () => {
        navigate('/teacher/settings');
      };
      return <SidebarWithAccordion {...sidebarConfig} onSettingsClick={handleSettingsClick} />;
    })()
  );

  const handleCreateNotebook = async () => {
    if (!newNotebookTitle.trim()) {
      alert('请输入笔记本标题');
      return;
    }

    const code = courseCode || selectedCourse?.code;
    if (!code) {
      alert('请先选择课程');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post('/notes/create', {
        courseCode: code.toUpperCase(),
        title: newNotebookTitle.trim(),
        description: newNotebookDescription.trim() || null
      });

      setCreateDialogOpen(false);
      setNewNotebookTitle('');
      setNewNotebookDescription('');
      fetchNotebooks();
      
      // Navigate to the new notebook
      if (response.data.notebook.id) {
        navigate(`/notebook/${response.data.notebook.id}`);
      }
    } catch (error) {
      console.error('Error creating notebook:', error);
      alert(error.response?.data?.error || '创建笔记本失败，请重试');
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (notebook, e) => {
    e.stopPropagation();
    setEditingNotebook(notebook);
    setNewNotebookTitle(notebook.title);
    setNewNotebookDescription(notebook.description || '');
    setEditDialogOpen(true);
  };

  const handleUpdateNotebook = async () => {
    if (!editingNotebook || !newNotebookTitle.trim()) {
      alert('请输入笔记本标题');
      return;
    }

    setUpdating(true);
    try {
      await axios.put(`/notes/${editingNotebook.id}`, {
        title: newNotebookTitle.trim(),
        description: newNotebookDescription.trim() || null
      });

      setEditDialogOpen(false);
      setEditingNotebook(null);
      setNewNotebookTitle('');
      setNewNotebookDescription('');
      fetchNotebooks();
    } catch (error) {
      console.error('Error updating notebook:', error);
      alert(error.response?.data?.error || '更新笔记本失败，请重试');
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadAllNotebooks = async () => {
    const code = courseCode || selectedCourse?.code;
    if (!code) {
      alert('请先选择课程');
      return;
    }
    setDownloadingAll(true);
    try {
      const response = await axios.get('/notes/download-all', {
        params: { courseCode: code.toUpperCase() },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `notebooks-${code.toUpperCase()}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Download all error:', error);
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          alert(json.error || '下载失败，请重试');
        } catch {
          alert('下载失败，请重试');
        }
      } else {
        alert(error.response?.data?.error || '下载失败，请重试');
      }
    } finally {
      setDownloadingAll(false);
    }
  };

  // AppBar actions
  const appBarActions = (
    <Stack direction="row" spacing={2} alignItems="center">
      <Chip 
        label={selectedCourse?.code || courseCode} 
        color="primary" 
        variant="outlined" 
      />
      {user?.role === 'teacher' && (
        <>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            color="primary"
          >
            创建空白笔记本
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => navigate('/upload-notebook')}
            color="primary"
          >
            上传笔记
          </Button>
          <Button
            variant="outlined"
            startIcon={downloadingAll ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadAllNotebooks}
            disabled={downloadingAll || notebooks.length === 0}
            color="primary"
          >
            {downloadingAll ? '打包中...' : '下载全部笔记本'}
          </Button>
        </>
      )}
    </Stack>
  );

  return (
    <DashboardLayout
      drawerContent={drawerContent}
      appBarTitle="课程笔记"
      appBarActions={appBarActions}
      courseName={selectedCourse?.name}
      courseTermCode={selectedCourse?.termCode}
      mobileOpen={mobileOpen}
      onDrawerToggle={handleDrawerToggle}
    >
      <Container maxWidth="lg" sx={{ pt: 2, pb: 4 }}>
        {/* 主内容区顶部：添加空白笔记本、导入笔记本 */}
        {user?.role === 'teacher' && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              color="primary"
              sx={{ textTransform: 'none' }}
            >
              添加空白笔记本
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => navigate('/upload-notebook')}
              color="primary"
              sx={{ textTransform: 'none' }}
            >
              导入笔记本
            </Button>
            <Button
              variant="outlined"
              startIcon={downloadingAll ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadAllNotebooks}
              disabled={downloadingAll || notebooks.length === 0}
              color="primary"
              sx={{ textTransform: 'none' }}
            >
              {downloadingAll ? '打包中...' : '下载全部笔记本'}
            </Button>
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : notebooks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Book sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              暂无笔记
            </Typography>
            <Typography variant="body2" color="text.secondary">
              该课程还没有可用的笔记
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {notebooks.map((notebook) => (
              <Grid item xs={12} sm={6} md={4} key={notebook.id}>
                <Card
                  sx={cardSx}
                  onClick={() => handleNotebookClick(notebook.id)}
                >
                  <CardMedia
                    sx={{ height: 140, position: 'relative', zIndex: 0 }}
                    image={getNotebookImage(notebook.title)}
                    title={notebook.title}
                  />
                  <CardContent sx={cardContentSx}>
                    <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                        <NotebookAgentAvatar agentAvatar={notebook.agentAvatar} notebookId={notebook.id} size={48} />
                        <Typography variant="h6" component="h2" fontWeight="600" noWrap>
                          {notebook.title}
                        </Typography>
                      </Stack>
                      {user?.role === 'teacher' && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => handleEditClick(notebook, e)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 2
                      }}
                    >
                      {notebook.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={cardActionsSx}>
                    <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                      <Button 
                        size="small" 
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/questions/notebook/${notebook.notebookId ?? notebook.id}`);
                        }}
                        sx={{ flex: 1 }}
                      >
                        查看题目
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/notebook/${notebook.notebookId ?? notebook.id}/chat`);
                        }}
                        sx={{ flex: 1 }}
                      >
                        开始聊天
                      </Button>
                      {user?.role === 'teacher' && (
                        <Button 
                          size="small" 
                          variant="outlined"
                          color="error"
                          onClick={(e) => handleDeleteClick(notebook, e)}
                          startIcon={<DeleteIcon />}
                        >
                          删除
                        </Button>
                      )}
                    </Stack>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>上传笔记</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              请上传笔记文件（支持 JSON、Markdown 或文本格式）。非 JSON 格式的文件将自动转换为 JSON 格式并导入到数据库中。
            </Alert>

            {uploadError && (
              <Alert severity="error">{uploadError}</Alert>
            )}

            {uploadSuccess && (
              <Alert severity="success">{uploadSuccess}</Alert>
            )}

            <Box>
              <input
                accept=".json,.md,.txt,.markdown,.pdf,.doc,.docx,.ppt,.pptx"
                style={{ display: 'none' }}
                id="notebook-upload-input"
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <label htmlFor="notebook-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  disabled={uploading}
                  startIcon={<CloudUpload />}
                >
                  选择笔记文件
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                支持 JSON、Markdown、文本、PDF、Word(.docx)、PowerPoint(.pptx)，文件大小不超过 50MB
              </Typography>
            </Box>

            {selectedFile && (
              <Alert severity="success">
                已选择文件: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Alert>
            )}

            <TextField
              label="课程代码"
              value={courseCode || selectedCourse?.code || ''}
              disabled
              fullWidth
              helperText="笔记将导入到此课程"
            />

            {uploading && (
              <Box>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  正在上传并导入笔记...
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} disabled={uploading}>
            取消
          </Button>
          <Button 
            onClick={handleUpload} 
            variant="contained" 
            disabled={!selectedFile || uploading}
            startIcon={<CloudUpload />}
          >
            {uploading ? '上传中...' : '上传'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除笔记 <strong>"{notebookToDelete?.title}"</strong> 吗？
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            此操作将永久删除笔记及其所有练习题，且无法恢复。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            取消
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleting}
            startIcon={<DeleteIcon />}
          >
            {deleting ? '删除中...' : '确认删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Notebook Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => {
          setCreateDialogOpen(false);
          setNewNotebookTitle('');
          setNewNotebookDescription('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>创建空白笔记本</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="标题 *"
              fullWidth
              value={newNotebookTitle}
              onChange={(e) => setNewNotebookTitle(e.target.value)}
              placeholder="输入笔记本标题"
              disabled={creating}
            />
            <TextField
              label="描述"
              fullWidth
              multiline
              rows={4}
              value={newNotebookDescription}
              onChange={(e) => setNewNotebookDescription(e.target.value)}
              placeholder="输入笔记本描述（可选）"
              disabled={creating}
            />
            <TextField
              label="课程代码"
              value={courseCode || selectedCourse?.code || ''}
              disabled
              fullWidth
              helperText="笔记本将创建到此课程"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCreateDialogOpen(false);
              setNewNotebookTitle('');
              setNewNotebookDescription('');
            }} 
            disabled={creating}
          >
            取消
          </Button>
          <Button 
            onClick={handleCreateNotebook} 
            variant="contained" 
            disabled={!newNotebookTitle.trim() || creating}
            startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creating ? '创建中...' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Notebook Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => {
          setEditDialogOpen(false);
          setEditingNotebook(null);
          setNewNotebookTitle('');
          setNewNotebookDescription('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>编辑笔记本</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="标题 *"
              fullWidth
              value={newNotebookTitle}
              onChange={(e) => setNewNotebookTitle(e.target.value)}
              placeholder="输入笔记本标题"
              disabled={updating}
            />
            <TextField
              label="描述"
              fullWidth
              multiline
              rows={4}
              value={newNotebookDescription}
              onChange={(e) => setNewNotebookDescription(e.target.value)}
              placeholder="输入笔记本描述（可选）"
              disabled={updating}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setEditDialogOpen(false);
              setEditingNotebook(null);
              setNewNotebookTitle('');
              setNewNotebookDescription('');
            }} 
            disabled={updating}
          >
            取消
          </Button>
          <Button 
            onClick={handleUpdateNotebook} 
            variant="contained" 
            disabled={!newNotebookTitle.trim() || updating}
            startIcon={updating ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {updating ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default NotebookList;
